import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home, Map, Users, ClipboardList } from 'lucide-react';
import { useAppSelector } from '@/store/hooks';
import { tokenVerificationSelector } from '@/store/auth';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
} from '@/components/ui/sidebar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { NavUser } from '@/components/fragment/NavUser';

const navigationItems = [
  {
    title: 'Home',
    url: '/',
    icon: Home,
  },
  {
    title: 'Map',
    url: '/map',
    icon: Map,
  },
];

const collapsibleGroups = [
  {
    title: 'People Management',
    icon: Users,
    items: [
      { title: 'Personnel', url: '/personnel' },
      { title: 'Teams', url: '/teams' },
    ],
  },
  {
    title: 'Assessment',
    icon: ClipboardList,
    items: [
      { title: 'Rapid Assessment', url: '/assessment/rapid' },
      { title: 'Detailed Assessment', url: '/assessment/detailed' },
    ],
  },
];

export function AppSidebar() {
  const location = useLocation();
  const accountInfo = useAppSelector(tokenVerificationSelector);

  const user: { firstName: string; lastName: string; email: string } = useMemo(() => {
    if (
      accountInfo &&
      accountInfo?.pvPersonGivenName &&
      accountInfo?.pvPersonSurName &&
      accountInfo?.email
    ) {
      return {
        firstName: accountInfo.pvPersonGivenName,
        lastName: accountInfo.pvPersonSurName,
        email: accountInfo.email,
      };
    }
    return { firstName: '', lastName: '', email: '' };
  }, [accountInfo]);

  const isActive = (url: string) => {
    if (url === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(url);
  };

  return (
    <Sidebar collapsible='offcanvas'>
      <SidebarHeader className='bg-primary h-14'>
        <div className='flex items-center gap-2 p-2'>
          <img
            src='/images/launcher.png'
            alt='Palmetto Logo'
            className='h-8 w-auto object-contain'
          />
          <span className='text-primary-foreground text-xs font-semibold group-data-[collapsible=icon]:hidden'>
            {import.meta.env.VITE_APP_TITLE}
          </span>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navigationItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={isActive(item.url)} tooltip={item.title}>
                    <Link to={item.url}>
                      <item.icon />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}

              {collapsibleGroups.map((group) => (
                  <Collapsible
                    key={group.title}
                    asChild
                    defaultOpen
                    className='group/collapsible'>
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton tooltip={group.title}>
                          <group.icon />
                          <span>{group.title}</span>
                          <ChevronRight className='ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90' />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {group.items.map((subItem) => (
                            <SidebarMenuSubItem key={subItem.title}>
                              <SidebarMenuSubButton asChild isActive={isActive(subItem.url)}>
                                <Link to={subItem.url}>
                                  <span>{subItem.title}</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          ))}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <NavUser user={user} />
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
